import { t, te } from "@/language/translation";
import { ErrorLink } from "@apollo/client/link/error";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/vue";
import { createPersistedQueryLink } from "@apollo/client/link/persisted-queries";
import { ApolloClient, ApolloLink } from "@apollo/client/core";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import cache from "./apolloCache";
import { transactionId } from "@/sentry";
import { keycloak } from "@/keycloak/Keycloak";
import store from "@/store";

async function hashSHA256(input: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

const httpLinkConfig = {
  uri: `${window.location.origin}/graphql`,
  headers: {
    "X-Version": import.meta.env.__VERSION__,
  },
};

const batchHttpLink = new BatchHttpLink(httpLinkConfig);

const nonBatchedHttpLink = createUploadLink({
  ...httpLinkConfig,
  headers: {
    ...httpLinkConfig.headers,
    "Apollo-Require-Preflight": "true",
  },
}) as unknown as ApolloLink;

const transactionIdMiddleware = new ApolloLink((operation, forward) => {
  const id = transactionId();
  // add adds a random Id to the header
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      "X-Transaction-ID": id,
    },
  }));

  // generate unique transactionId and set as Sentry tag
  Sentry.getCurrentScope().setTag("transaction_id", id);

  return forward(operation);
}).concat(nonBatchedHttpLink);

const batchableQueries = [
  "SearchSite",
  "TCUserVacationStatistics",
  "GetChartData",
  "BDEGetProductionPlanInRange",
  "BDESystemStatus",
  "GetIHSeriesForIHParetoChart",
  "GSGBDECustomerRequirements",
  "BDEGetQAChecklistForProductionPlan",
  "BDEGetActiveShift",
  "GetAllMQTTRuleForGantt",
  "BDEGetChartLayoutForLiveOEE",
  "AuditQuestions",
  "EABShorthandsAndWorkflowForBoards",
  "IHConfig",
  "HLPConfigForSite",
  "SQDCWorkflowInstances",
  "SQDCWorkflowDefinitions",
  "SQDCEABShorthands",
  "GetUserForRevisionList",
  "SQDCActionDeadlineChanges",
];

const httpLink = ApolloLink.split(
  // split based on operation type
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "query" &&
      batchableQueries.includes(definition.name?.value ?? "")
    );
  },
  batchHttpLink,
  transactionIdMiddleware,
);
let persistedHttpLink;

if (import.meta.env.PROD) {
  persistedHttpLink = createPersistedQueryLink({
    sha256: hashSHA256,
    disable({ graphQLErrors }) {
      if (import.meta.env.VITE_APP_DISABLE_PERSISTED_QUERIES || import.meta.env.MODE === "test") {
        return true;
      }
      // syntax after update to apollo client version >= 4
      // if (
      //   (CombinedGraphQLErrors.is(graphQLErrors) || CombinedProtocolErrors.is(graphQLErrors)) &&
      //   !graphQLErrors.errors.some((error) => error.message === "PersistedQueryNotSupported")
      // ) {
      if (
        graphQLErrors &&
        graphQLErrors.some((error) => {
          const { message } = error;
          return message === "PersistedQueryNotSupported";
        })
      ) {
        return true;
      }

      return false;
    },
  }).concat(httpLink);
}

const authedHttpLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => {
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${keycloak?.token}`,
      },
    };
  });

  return forward(operation);
}).concat(persistedHttpLink ?? httpLink);

const errorLink = new ErrorLink(
  ({ operation, forward, networkError, graphQLErrors, protocolErrors }) => {
    if (
      ["SQDCBoardGetName", "GetSiteIdForBoard", "EABShorthandsAndWorkflowForBoards"].includes(
        operation.operationName as string,
      )
    ) {
      return;
    }

    function handleError(message: string) {
      if (message === "VALID_LOGIN_NO_LISA_USER") {
        store.dispatch("login/setSetup", { state: true });
        return;
      }
      if (
        message === "missingSession" ||
        message === "expiredSession" ||
        message === "invalidSession"
      ) {
        if (store.state.login.user) {
          store.dispatch("login/logout");
          window.$toast?.({
            severity: "error",
            summary: t("api.toast.error.title").toString(),
            detail: t("api.toast.error.expiredSession").toString(),
          });
        }
      }

      let errorMessage;
      if (te(`api.toast.error.${message}`)) {
        errorMessage = t(`api.toast.error.${message}`);
      } else {
        errorMessage = t("api.toast.error.common");
      }
      window.$toast?.({
        severity: "error",
        life: 5000,
        summary: t("api.toast.error.title").toString(),
        detail: errorMessage?.toString(),
      });
    }

    if (graphQLErrors != null /**CombinedGraphQLErrors.is(error)*/) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        // eslint-disable-next-line no-console
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
        handleError(message);
      });
    }
    if (protocolErrors != null /**CombinedProtocolErrors.is(error)*/) {
      protocolErrors.forEach(({ message, extensions }) => {
        // eslint-disable-next-line no-console
        console.error(
          `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`,
        );
        handleError(message);
      });
    }
    //else {
    if (networkError != null) {
      // eslint-disable-next-line no-console
      console.error(`[Network error]: ${networkError}`);
      window.$toast?.({
        severity: "error",
        detail: t("api.toast.error.networkError").toString(),
        summary: t("api.toast.error.title").toString(),
      });
    }

    forward(operation);
  },
);

const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";

// Create the subscription websocket link
export const wsLink = new GraphQLWsLink(
  createClient({
    url: `${wsProtocol}//${window.location.host}/graphql`,
    shouldRetry: () => true,
    retryAttempts: 5,
    connectionParams: async () => {
      return {
        authorization: `Bearer ${keycloak.token}`,
      };
    },
  }),
);

// using the ability to split links, you can send data to each link
// depending on what kind of operation is being sent
export function getApolloClientLink() {
  return ApolloLink.split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === "OperationDefinition" && definition.operation === "subscription";
    },
    wsLink,
    authedHttpLink,
  );
}

export const apolloClient = new ApolloClient({
  clientAwareness: {
    name: "lisapp-frontend",
    version: import.meta.env.__VERSION__,
  },
  link: errorLink.concat(getApolloClientLink()),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    },
  },
});

export default {
  apolloClient,
};
